import { useState, useCallback, useRef, useEffect } from 'react';
import type { TimerState, TimerConfig } from '../engine/types';
import { TimerController, getDefaultTimerState, formatTime, formatTimeLong } from '../engine/timer';

export interface UseTimerOptions {
  contentId: string;
  duration: number;
  warningThreshold?: number;
  autoSubmitOnExpire?: boolean;
  autoStart?: boolean;
  onTick?: (state: TimerState) => void;
  onWarning?: (remaining: number) => void;
  onExpire?: () => void;
}

export interface UseTimerReturn {
  // State
  state: TimerState;
  remaining: number;
  elapsed: number;
  isRunning: boolean;
  isPaused: boolean;
  isExpired: boolean;
  isWarning: boolean;
  
  // Formatted time
  formattedTime: string;
  formattedTimeLong: string;
  progress: number;
  
  // Actions
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  reset: () => void;
  addTime: (seconds: number) => void;
  subtractTime: (seconds: number) => void;
  setDuration: (duration: number) => void;
  
  // Controller access
  controller: TimerController;
}

/**
 * Hook for managing content timer
 */
export function useTimer(options: UseTimerOptions): UseTimerReturn {
  const {
    contentId,
    duration,
    warningThreshold = 30,
    autoSubmitOnExpire = false,
    autoStart = false,
    onTick,
    onWarning,
    onExpire,
  } = options;

  // Create controller ref
  const controllerRef = useRef<TimerController>();
  if (!controllerRef.current) {
    const config: TimerConfig = {
      duration,
      warningThreshold,
      autoSubmitOnExpire,
      onTick,
      onWarning,
      onExpire,
    };
    controllerRef.current = new TimerController(contentId, config);
  }
  const controller = controllerRef.current;

  // State
  const [state, setState] = useState<TimerState>(() => controller.getState());

  // Subscribe to controller events
  useEffect(() => {
    const unsubscribe = controller.onAny(() => {
      setState(controller.getState());
    });

    return () => {
      unsubscribe();
    };
  }, [controller]);

  // Auto start
  useEffect(() => {
    if (autoStart) {
      controller.start();
    }
  }, [autoStart, controller]);

  // Actions
  const start = useCallback(() => {
    controller.start();
  }, [controller]);

  const pause = useCallback(() => {
    controller.pause();
  }, [controller]);

  const resume = useCallback(() => {
    controller.resume();
  }, [controller]);

  const stop = useCallback(() => {
    controller.stop();
    setState(controller.getState());
  }, [controller]);

  const reset = useCallback(() => {
    controller.reset();
  }, [controller]);

  const addTime = useCallback((seconds: number) => {
    controller.addTime(seconds);
    setState(controller.getState());
  }, [controller]);

  const subtractTime = useCallback((seconds: number) => {
    controller.subtractTime(seconds);
  }, [controller]);

  const setDuration = useCallback((newDuration: number) => {
    controller.setDuration(newDuration);
    setState(controller.getState());
  }, [controller]);

  // Cleanup
  useEffect(() => {
    return () => {
      controller.destroy();
    };
  }, [controller]);

  return {
    // State
    state,
    remaining: state.remaining,
    elapsed: state.elapsed,
    isRunning: state.isRunning,
    isPaused: state.isPaused,
    isExpired: state.isExpired,
    isWarning: controller.isWarning(),
    
    // Formatted time
    formattedTime: controller.getFormattedTime(),
    formattedTimeLong: controller.getFormattedTimeLong(),
    progress: controller.getProgress(),
    
    // Actions
    start,
    pause,
    resume,
    stop,
    reset,
    addTime,
    subtractTime,
    setDuration,
    
    // Controller access
    controller,
  };
}

// Re-export formatting utilities
export { formatTime, formatTimeLong };
